import React, { useEffect, useRef, useState } from "react";
import styles from './AppListSide.module.scss'
import Button from "../../components/ripple/Button";
import Input from "../../components/Input";
import showModalDefault from "../../helpers/ui/ModalDefault";
import { immutableArrayItemUpdate } from "../../helpers/common";

function AppListSideBlock({title, children, button}) {
    return (
        <div className={styles.appsSideBlock}>
            <div className={styles.appsSideBlock__title}>
                <p>{title}</p>
                {
                    !!button?.showIf &&
                    <Button
                        className={styles.appsSideBlock__title__button}
                        displayType='text'
                        icon='fas fa-undo-alt'
                        visuals={{height: '2.5rem', colorBasic: ['var(--colorFontInfo)']}}
                        onClick={button?.onClick}
                    />
                }
            </div>
            {children}
        </div>
    )
}


function AppListFilters({setLoading, ...props}) {

    const [order, setOrder] = useState(props.order); // this is required when in modal on mobile devices

    useEffect(() => {
        setOrder(props.order);
    }, [props.order]);

    const renderTags = () => (
        order.tags.map((tag, tagIndex) => {

            const isActive = tag.active;
            let className = styles.appsSideBlock__button;
            className += isActive
                ? ` ${styles['appsSideBlock__button--active']}`
                : '';

            return (
                <Button
                    title={tag.title}
                    iconRight='fas fa-times'
                    onClick={() => {
                        const tags = immutableArrayItemUpdate(order.tags, tag, {active: !tag.active});
                        setOrder({...order, ...{tags}})
                        props.setOrder({tags})
                    }}
                    displayType='text'
                    className={className}
                    visuals={{
                        colorFontNotContained: ['var(--colorFontCommon)'],
                        colorBasic: ['var(--colorFontInfo)'],
                        borderRadius: '0',
                        height: '3.5rem',
                    }}
                    key={tagIndex}
                />
            )
        })
    );

    const orderClick = (newType, newCriterion) => {

        let defaultDescendant = newCriterion === 'popularity' || newCriterion === 'updated';

        if (order.type === newType && order.criterion === newCriterion) defaultDescendant = !order.descendant;

        const newOrder = {
            type: newType,
            criterion: newCriterion,
            descendant: defaultDescendant,
        };

        setOrder({...order, ...newOrder});
        props.setOrder(newOrder);

        if (newType !== order.type) setLoading(true);
    };

    const fillOrderButton = (_criterion, type, title) => {

        const isActive = order.criterion === _criterion;
        let className = styles['appsSideBlock__button'];
        className += isActive ? ` ${styles['appsSideBlock__button--active']}` : '';
        className += isActive && !order.descendant ? ` ${styles['appsSideBlock__button--ascendant']}` : ''

        return (
            <Button
                title={title ? title : _criterion.charAt(0).toUpperCase() + _criterion.slice(1)}
                iconRight='fas fa-chevron-down'
                onClick={() => orderClick(type, _criterion)}
                displayType='text'
                className={className}
                visuals={{
                    colorFontNotContained: ['var(--colorFontCommon)'],
                    colorBasic: ['var(--colorFontInfo)'],
                    borderRadius: '0',
                    height: '3.5rem',
                }}
            />
        )
    };

    return (
        <>
            <AppListSideBlock title='Sort by'>
                {fillOrderButton('popularity', 'sort')}
                {fillOrderButton('name', 'sort')}
                {fillOrderButton('updated', 'sort', 'Update date')}
            </AppListSideBlock>
            <AppListSideBlock title='Group by'>
                {fillOrderButton('category', 'group', 'Category')}
            </AppListSideBlock>
            <AppListSideBlock
                title='Filter by category'
                button={{
                    showIf: order.tags.some(tag => !!tag.active),
                    onClick: () => {
                        const newTags = order.tags.map(t => ({...t, ...{active: false}}));
                        setOrder({...order, ...{tags: newTags}});
                        props.setOrder({tags: newTags});
                    }
                }}
            >
                {renderTags()}
            </AppListSideBlock>
        </>
    )
}

export default function AppListSide({
                                        search,
                                        width,
                                        setSearch,
                                        order,
                                        setOrder,
                                        setLoading,
                                    }) {

    const isMobile = width <= 830;

    const inputRef = useRef();

    const [focus, setFocus] = useState(!isMobile);

    const renderFilters = () => (
        <AppListFilters
            order={order}
            setOrder={setOrder}
            setLoading={setLoading}
        />
    )

    return (
        <aside className={styles.appsSide}>
            <div className={styles.appsSide__header}>
                <form
                    className={`${styles.search}${!search ? ' ' + styles['search--empty'] : ''}`}
                    onSubmit={e => e.preventDefault()}
                >
                    <Input
                        className={styles.search__field}
                        type="text"
                        // placeholder="Type to search..."
                        placeholder='Search...'
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onFocus={() => setFocus(true)}
                        onBlur={() => setFocus(false)}
                        autofocus={!isMobile}
                        aria-label="Search"
                        iconRight='fas fa-search'
                        visualVariables={{
                            height: '5rem',
                            borderRadius: 'var(--borderRadiusContainerInnerEdge, 24px)',
                            colorBackground: focus ? 'var(--colorBackgroundContent)' : 'var(--colorBackgroundContent)',
                            colorBorder: 'var(--colorBackgroundContent)',
                            colorBorderHover: 'var(--colorAccentLight)',
                        }}
                        ref={inputRef}
                    />
                    <Button
                        className={styles.search__filters}
                        displayType="text"
                        icon="fas fa-sliders-h"
                        aria-label="Open filters"
                        visuals={{
                            colorBasic: ['var(--colorAccentLight)'],
                            borderRadius: 'var(--borderRadiusContainerInnerEdge, 30px)',
                            height: '4rem',
                        }}
                        onClick={() => {
                            showModalDefault({
                                title: 'Applications filter',
                                component: renderFilters(),
                            })
                        }}
                    />
                    <Button
                        className={styles.search__clear}
                        displayType="text"
                        icon="fas fa-times"
                        onClick={() => {
                            setSearch('');
                            inputRef?.current?.focus();
                        }}
                        onFocus={() => setFocus(true)}
                        onBlur={() => setFocus(false)}
                        aria-label="Clear search"
                        visuals={{
                            colorBasic: ['var(--colorAccentLight)'],
                            borderRadius: 'var(--borderRadiusContainerInnerEdge, 30px)',
                            height: '4rem',
                        }}
                    />
                </form>
            </div>
            <div className={styles.appsSide__content}>
                {renderFilters()}
            </div>
        </aside>
    )
}